import React from "react"
import tw, { styled } from "twin.macro"
import CopyLink from "./copyLink"
import { Description, Field, HelpText, InputType, Label, StyledSpinner } from "../input"

interface UsernameInputProps {
  inputValue: string
  name: string
  handleChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  handleFocus?: () => Promise<void> | void
  disabled?: boolean
  required?: boolean
  label?: string
  description?: string
  placeholder: string
  helpText?: string
  tabIndex?: number
  invalid?: boolean
  loading?: boolean
}
interface StyledProps {
  invalid: boolean | undefined
}

const StyledPrefix = styled("span")(({ invalid }: StyledProps) => [
  tw`px-4 absolute font-bold left-0 top-2.5`,
  invalid && tw`text-red`,
])
const StyledInput = styled.input(({ invalid }: StyledProps) => [
  tw`w-full p-2 pl-[122px]`,
  tw`text-base-6 bg-input caret-purple`,
  tw`(overflow-hidden whitespace-nowrap overflow-ellipsis)`,
  tw`(border border-base-3 rounded)`,
  tw`placeholder:(text-base-3)`,
  tw`focus:(outline-none border-purple)`,
  tw`disabled:(text-base-2 border-base-2 placeholder:(text-base-2))`,
  tw`read-only:(focus:border-base-3)`,
  invalid && tw`(border-red)`,
])

export const UsernameInput = ({
  inputValue,
  name,
  handleChange,
  handleKeyDown,
  handleFocus,
  disabled,
  required,
  tabIndex,
  label,
  description,
  placeholder,
  helpText,
  invalid,
  loading,
}: UsernameInputProps) => {
  const renderCopyLink = !!inputValue && !loading && !invalid

  return (
    <Field>
      <Label data-testid={`${name}-label`} htmlFor={`${name}-id`}>
        {label}
      </Label>
      {description && (
        <Description id={`${name}-description`} data-testid={`${name}-description`}>
          {description}
        </Description>
      )}
      <div tw="relative">
        <StyledPrefix invalid={invalid}>joinbonsai.co/</StyledPrefix>
        <StyledInput
          data-testid={`${name}-input`}
          autoComplete="off" // value has dashes will not work in chrome
          tabIndex={tabIndex}
          type={InputType.Text}
          name={name}
          id={`${name}-id`}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          value={inputValue}
          placeholder={placeholder}
          aria-label={label}
          aria-describedby={`${name}-description ${name}-helptext`}
          disabled={disabled || loading}
          required={required}
          aria-required={required}
          invalid={invalid}
        />
        {loading && <StyledSpinner data-testid="spinner" />}
        {renderCopyLink && <CopyLink username={inputValue} data-testid="copy-link" />}
      </div>
      <HelpText id={`${name}-helptext`} data-testid={`${name}-help-text`} invalid={invalid}>
        {helpText}
      </HelpText>
    </Field>
  )
}
